import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Game } from '@app/shared/models/game.model';
import { HelperService } from '@app/shared/services/helper.service';
import { environment } from '@environments/environment';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { TesterModalService } from '../tester-modal.service';
import { TesterModalFacade } from '../tester-modal.facade';
import { HighScore } from '@app/shared/models/user.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { RealtimeService } from '@app/shared/services/realtime.service';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { TesterRepository } from '@app/shared/state/tester/tester.repository';

@Component({
  standalone: true,
  selector: 'slotcom-tester-player-options',
  imports: [FormsModule, SharedModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './player-options.component.html',
  styleUrls: ['../tester-modal.component.scss'],
})
export class PlayerOptionsComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  helperService = inject(HelperService);
  private testerModalService = inject(TesterModalService);
  private testerModalFacade = inject(TesterModalFacade);
  private loggerService = inject(LoggerService);
  private realtimeService = inject(RealtimeService);
  private userRepo = inject(UserRepository);
  private testerRepo = inject(TesterRepository);

  blockUserBuys: boolean = false;
  blockUserBuysForm: FormGroup;

  selectedVersion: string;
  versions: string[];
  emailValue: string;
  showTestMachinesForm: FormGroup;
  versionForm: FormGroup;
  logLevelForm: FormGroup;
  incrementProgressLineForm: FormGroup;
  unlockGameForm: FormGroup;
  changeEmailForm: FormGroup;

  sortedGames: Game[] = [];
  addCoinsValue: number;
  addGemsValue: number;

  selectedUnlockGame: string;

  incrementProgressLineValue: number;

  async ngOnInit(): Promise<void> {
    this.realtimeService.on('HighScore').subscribe({
      next: (highscore: HighScore) => {
        this.userRepo.updateUser({ highScore: highscore });
      },
      error: (error) => this.loggerService.error(error),
    });

    this.blockUserBuys = this.testerModalFacade.user().purchaseBlocked;
    this.sortedGames = this.testerModalFacade.getSortedGames();

    this.showTestMachinesForm = this.formBuilder.group({
      testMachines: [this.testerRepo.getShowTestMachines()],
    });

    this.testerRepo.showTestMachines$.subscribe((value) => {
      this.showTestMachinesForm.get('testMachines')?.setValue(value);
    });

    this.showTestMachinesForm.get('testMachines')?.valueChanges.subscribe((value) => {
      this.testerRepo.setShowTestMachines(value);
    });

    this.blockUserBuysForm = this.formBuilder.group({
      blockUserBuys: [this.blockUserBuys],
    });

    this.blockUserBuysForm.get('blockUserBuys')?.valueChanges.subscribe(async (value) => {
      this.blockUserBuys = value;
      await this.testerModalService.setPurchaseBlocked(value);
      await this.testerModalFacade.updateUser();
    });

    this.versionForm = this.formBuilder.group({
      selectedVersion: [this.selectedVersion],
    });

    this.versionForm.get('selectedVersion')?.valueChanges.subscribe((value) => {
      this.selectedVersion = value;
    });

    this.logLevelForm = this.formBuilder.group({
      selectedLogLevel: [environment.logLevel],
    });

    this.incrementProgressLineForm = this.formBuilder.group({
      incrementProgressLineValue: [this.incrementProgressLineValue],
    });
    this.incrementProgressLineForm.get('incrementProgressLineValue')?.valueChanges.subscribe((value) => {
      this.incrementProgressLineValue = parseInt(value);
    });

    this.changeEmailForm = this.formBuilder.group({
      emailValue: [this.emailValue],
    });

    this.changeEmailForm.get('emailValue')?.valueChanges.subscribe((value) => {
      this.emailValue = value;
    });

    this.unlockGameForm = this.formBuilder.group({
      selectedUnlockGame: [this.selectedUnlockGame],
    });
    this.unlockGameForm.get('selectedUnlockGame')?.valueChanges.subscribe((value) => {
      this.selectedUnlockGame = value.toString();
    });

    this.versions = await this.testerModalService.getFEVersions();
  }

  addCoinsToUser() {
    this.testerModalService.addCredits(this.addCoinsValue);
  }

  addGemsToUser() {
    this.testerModalService.addGems(this.addGemsValue);
  }

  copyUserActualCoins() {
    this.testerModalFacade.copyWallet('Credits');
  }

  copyUserActualGems() {
    this.testerModalFacade.copyWallet('Gems');
  }

  async addPrestige(): Promise<void> {
    await this.testerModalService.addPrestige(this.incrementProgressLineValue);
  }

  onSubmitVersion(): void {
    this.testerModalFacade.reloadQueryParams(this.selectedVersion);
  }

  async deleteEmailLogin(): Promise<void> {
    await this.testerModalService.deleteEmailLogin();
  }

  async deleteFacebookLogin(): Promise<void> {
    await this.testerModalService.deleteFacebookLogin();
  }

  async deleteAppleLogin(): Promise<void> {
    await this.testerModalService.deleteAppleLogin();
  }

  async deleteGooglePlayLogin(): Promise<void> {
    await this.testerModalService.deleteGooglePlayLogin();
  }

  async deleteGuestLogin(): Promise<void> {
    await this.testerModalService.deleteGuestLogin();
  }

  async updateUserEmail(): Promise<void> {
    await this.testerModalService.updateEmail(this.emailValue);
  }

  async unlockGame(): Promise<void> {
    await this.testerModalService.unlockGame(this.selectedUnlockGame);
    this.testerModalFacade.manageNewUnlockedTag();
  }

  async resetGoldenBomb(): Promise<void> {
    await this.testerModalService.resetGoldenBomb();
  }

  async resetRecurring(): Promise<void> {
    await this.testerModalService.resetRecurring();
  }

  async setLastLoginYesterday(): Promise<void> {
    await this.testerModalService.setLastLoginYesterday();
  }

  async resetHighScore(): Promise<void> {
    await this.testerModalService.resetHighScore();
  }

  async deleteEventStatus(): Promise<void> {
    await this.testerModalService.deleteAllUserLiveopStatuses();
  }

  async resetAll(): Promise<void> {
    await this.testerModalService.resetAll();
  }

  async unlockAllGames(): Promise<void> {
    await this.testerModalService.unlockAllGames();
    this.testerModalFacade.manageNewUnlockedTag();
  }
}
